// src/components/ClientLogin.jsx
import React, { useState, useEffect } from 'react';
import '../styles/Login.css';
import logo from '../logo512.png';
import { apiFetch } from '../api/apiClient';
import LoginCodeStep from './LoginCodeStep';

// Matches the mockup's own hand-drawn eye icon exactly (a simple
// eye-outline + pupil), rather than a FontAwesome-style icon - this one
// is shown as part of 6a/6b itself, not a decorative addition.
const EyeIcon = () => (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="#33565C" strokeWidth="1.5">
    <path d="M1.8 10S4.9 4.6 10 4.6 18.2 10 18.2 10 15.1 15.4 10 15.4 1.8 10 1.8 10z"></path>
    <circle cx="10" cy="10" r="2.4"></circle>
  </svg>
);

const ClientLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [challenge, setChallenge] = useState(null); // set once the password is accepted

  useEffect(() => {
    const handleEnterKey = (e) => {
      if (e.key === 'Enter' && !isLoading && !challenge && (username && password)) {
        const form = e.target.closest('form');
        if (form) {
          const submitEvent = new Event('submit', { cancelable: true });
          form.dispatchEvent(submitEvent);
        }
      }
    };

    document.addEventListener('keydown', handleEnterKey);
    return () => {
      document.removeEventListener('keydown', handleEnterKey);
    };
  }, [isLoading, username, password, challenge]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username.trim() || !password.trim()) {
      setError('Please enter your username or email, and your password');
      return;
    }
    
    setError('');
    setIsLoading(true);

    try {
      const response = await apiFetch('/api/auth/client/login', {
        method: 'POST',
        skipAuthRefresh: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.mfa_required) {
          setChallenge(data); // password OK - now ask for the emailed code
        } else {
          onLogin(data.user); // server has MFA_ENABLED=false
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Invalid credentials');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
      console.error('Client login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (challenge) {
    return (
      <LoginCodeStep
        challenge={challenge}
        containerClass="client-login"
        onVerified={(data) => onLogin(data.user)}
        onCancel={() => { setChallenge(null); setPassword(''); }}
      />
    );
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-page">
      <div className="login-container client-login">
        <div className="login-header">
          <img src={logo} className="login-logo-247" alt="247 Logo" />
          <div>
            <h2>Client Portal</h2>
            <p>Twenty4Seven Monitoring System</p>
          </div>
        </div>
        
        {error && (
          <div className="login-error">
            <span className="login-error-icon">!</span>
            <div>{error}</div>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username or email</label>
            <input 
              type="text" 
              id="username"
              autoComplete="username"
              autoCapitalize="none"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required 
              autoFocus
              disabled={isLoading}
            />
          </div>
          
          <div className="form-group password-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input 
                type={showPassword ? "text" : "password"} 
                id="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
              <button 
                type="button" 
                className="password-toggle"
                onClick={togglePasswordVisibility}
                disabled={isLoading}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <EyeIcon />
              </button>
            </div>
          </div>

          <button 
            type="submit" 
            className="login-btn" 
            disabled={isLoading || !username.trim() || !password.trim()}
          >
            {isLoading ? (
              <>
                <span className="spinner-icon">↻</span>
                Logging in...
              </>
            ) : (
              'Login'
            )}
          </button>
        </form>
        
        <div className="login-info">
          <p>Need help? Contact your system administrator<br />
          <a href="mailto:support@twenty4seven.io">support@twenty4seven.io</a></p>
        </div>
      </div>
    </div>
  );
};

export default ClientLogin;
